import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

export default function TestimonialsSection() {
  const [isDesktop, setIsDesktop] = useState(false);
  
  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 768);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  const testimonials = [
    {
      name: 'Marc Dubois',
      position: 'Directeur Technique',
      company: 'Industrie Métallurgique Lyon',
      avatar: 'https://public.readdy.ai/ai/img_res/e7400cb802e181ab1c0c7c842654a9b2.jpg',
      content: 'L\'installation LED a dépassé nos attentes. Nous avons réduit notre facture d\'éclairage de 70% et l\'ambiance de travail s\'est considérablement améliorée.',
      rating: 5,
      savings: '70%',
      gradient: 'from-emerald-400 to-teal-600',
      initials: 'MD',
      quote: 'L\'installation LED a dépassé nos attentes. Nous avons réduit notre facture d\'éclairage de 70% et l\'ambiance de travail s\'est considérablement améliorée.'
    },
    {
      name: 'Sophie Martin',
      position: 'Responsable Maintenance',
      company: 'Entrepôt Logistique Paris',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20french%20business%20woman%20portrait%2040%20years%20old%20confident%20smile%20formal%20attire%20clean%20simple%20light%20background%20studio%20photography%20high%20quality%20sharp%20details%208k&width=200&height=200&seq=avatar-woman-002&orientation=squarish',
      content: 'Financement à 1€ avec les CEE, installation rapide et professionnelle. Nos employés apprécient la qualité de l\'éclairage et nous économisons énormément.',
      rating: 5,
      savings: '68%',
      gradient: 'from-purple-400 to-pink-600',
      initials: 'SM',
      quote: 'Financement à 1€ avec les CEE, installation rapide et professionnelle. Nos employés apprécient la qualité de l\'éclairage et nous économisons énormément.'
    },
    {
      name: 'Jean-Pierre Leroy',
      position: 'Gérant',
      company: 'Atelier Mécanique Marseille',
      avatar: 'https://readdy.ai/api/search-image?query=professional%20french%20business%20man%20portrait%2055%20years%20old%20confident%20smile%20formal%20attire%20clean%20simple%20light%20background%20studio%20photography%20high%20quality%20sharp%20details%208k&width=200&height=200&seq=avatar-man-003&orientation=squarish',
      content: 'Très satisfait de cette rénovation LED. Le dossier CEE a été géré entièrement par leurs soins, ce qui nous a fait gagner un temps précieux. Je recommande vivement leurs services.',
      rating: 5,
      savings: '65%',
      gradient: 'from-blue-400 to-indigo-600',
      initials: 'JL',
      quote: 'Très satisfait de cette rénovation LED. Le dossier CEE a été géré entièrement par leurs soins, ce qui nous a fait gagner un temps précieux. Je recommande vivement leurs services.'
    },
  ];

  return (
    <section className="relative py-8 sm:py-12 lg:py-16 bg-gradient-to-b from-white to-slate-50 overflow-hidden">
      {/* Decorative Elements - Static on mobile */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/2 left-1/4 w-32 sm:w-48 lg:w-64 h-32 sm:h-48 lg:h-64 bg-emerald-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 right-1/4 w-32 sm:w-48 lg:w-64 h-32 sm:h-48 lg:h-64 bg-teal-500/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-4 sm:mb-6 lg:mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-1.5 bg-emerald-100 rounded-full px-3 py-1.5 mb-2 sm:mb-3"
          >
            <i className="ri-star-fill text-emerald-600 text-xs"></i>
            <span className="text-emerald-700 text-xs font-semibold">Témoignages clients</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-2 sm:mb-3"
          >
            Ils nous font confiance
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xs sm:text-sm text-slate-600 max-w-2xl mx-auto"
          >
            Découvrez les retours d'expérience de nos clients sur leurs projets de rénovation LED
          </motion.p>
        </div>

        {/* Testimonials Grid - Static on mobile */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={isDesktop ? { opacity: 0, y: 30 } : { opacity: 1, y: 0 }}
              whileInView={isDesktop ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
              transition={isDesktop ? { duration: 0.6, delay: index * 0.1 } : {}}
              viewport={isDesktop ? { once: true } : {}}
              whileHover={isDesktop ? { scale: 1.02, y: -2 } : {}}
              className={`bg-white/80 backdrop-blur-sm border border-slate-200 rounded-xl p-3 sm:p-4 lg:p-5 transition-all group cursor-pointer ${
                isDesktop ? 'hover:shadow-lg hover:border-emerald-300' : ''
              }`}
              style={!isDesktop ? { transform: 'none' } : {}}
            >
              {/* Header */}
              <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br ${testimonial.gradient} flex items-center justify-center text-white text-xs sm:text-sm font-bold flex-shrink-0`}>
                  {testimonial.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs sm:text-sm font-bold text-slate-900 mb-0.5">{testimonial.name}</h4>
                  <p className="text-xs text-slate-600 truncate">{testimonial.company}</p>
                </div>
                <div className="flex items-center gap-0.5 sm:gap-1 bg-emerald-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg">
                  <motion.i 
                    className="ri-arrow-down-line text-emerald-600 text-xs"
                    animate={isDesktop ? { rotate: [0, -10, 0] } : {}}
                    transition={isDesktop ? { duration: 2, repeat: Infinity } : {}}
                    style={!isDesktop ? { transform: 'none' } : {}}
                  ></motion.i>
                  <span className="text-xs font-bold text-emerald-700">{testimonial.savings}</span>
                </div>
              </div>

              {/* Quote */}
              <motion.div 
                className="relative"
                whileHover={isDesktop ? { scale: 1.01 } : {}}
                style={!isDesktop ? { transform: 'none' } : {}}
              >
                <motion.i 
                  className="ri-double-quotes-l text-emerald-500/30 text-xl sm:text-2xl absolute -top-1 -left-1"
                  animate={isDesktop ? { scale: [1, 1.1, 1] } : {}}
                  transition={isDesktop ? { duration: 3, repeat: Infinity, delay: index * 0.2 } : {}}
                  style={!isDesktop ? { transform: 'none' } : {}}
                ></motion.i>
                <p className="text-xs sm:text-sm text-slate-700 leading-relaxed pl-4 group-hover:text-slate-900 transition-colors">
                  {testimonial.quote}
                </p>
              </motion.div>

              {/* Rating */}
              <div className="flex justify-between items-center mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-slate-200">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <motion.i 
                      key={i}
                      className="ri-star-fill text-yellow-400 text-xs sm:text-sm"
                      animate={isDesktop ? { scale: [1, 1.2, 1] } : {}}
                      transition={isDesktop ? { duration: 0.5, delay: i * 0.1 + index * 0.2 } : {}}
                      style={!isDesktop ? { transform: 'none' } : {}}
                    ></motion.i>
                  ))}
                </div>
                <span className="text-xs text-slate-500 font-medium">Vérifié</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
